import { type RefObject, useEffect, useRef } from 'react';

/**
 * 163: put the first row of a list back in view when its page changes.
 *
 * 🔴 THIS MUST NOT USE `window.scrollTo`. The admin shell is `flex h-screen overflow-hidden`
 * (`App.tsx:270`) and the scroll lives on `<main className="flex-1 overflow-y-auto …">`
 * (`App.tsx:293`), so the window has no scroll to reset and `window.scrollTo(0, 0)` is a silent
 * no-op. It would look correct in review, pass any spy-based test, and do nothing in the panel.
 *
 * 🔴 Nor does it hard-code `document.querySelector('main')`. That works today and breaks the first
 * time a screen wraps its list in its own scroll region — which is exactly what features 137 and
 * 139 did to four customer-app screens. The scrolling ancestor is DISCOVERED from the element the
 * list actually rendered.
 *
 * Fires on a change of PAGE NUMBER only. Firing on "the rows changed" would also fire on every
 * background refetch and yank the list out from under someone mid-read; the cost of the narrower
 * rule is that a filter applied while already on page 1 does not scroll, which is stated as a
 * deliberate limit in the spec (FR-007) rather than left to be discovered.
 */
export function useScrollToTopOnPageChange(ref: RefObject<HTMLElement | null>, page: number): void {
  const previous = useRef(page);

  useEffect(() => {
    // Not on mount: `previous` starts at the current page, so the first run always returns here.
    // Arriving at a list should not move it, and a screen restoring a page from its own state
    // must not be fought on its first render.
    if (previous.current === page) return;
    previous.current = page;

    const container = scrollingAncestorOf(ref.current);
    // Assigned rather than `scrollTo({ behavior: 'instant' })`: assignment IS instant, needs no
    // option, and is observable — jsdom implements the property but not the method, so the
    // `scrollTo` form is untestable here and would have had to be taken on trust.
    //
    // Instant is deliberate. A smooth scroll on a list being paged through quickly is motion the
    // operator did not ask for, and would then owe `prefers-reduced-motion` handling.
    if (container) container.scrollTop = 0;
  }, [page, ref]);
}

/**
 * The nearest ancestor that actually scrolls, or `null`.
 *
 * Both conditions are needed. An `overflow-y: auto` element that fits its content does not scroll,
 * so its `scrollTop` is already 0 and stopping there would leave the real container untouched.
 *
 * 🔴 In jsdom `scrollHeight` and `clientHeight` are both 0, so this finds nothing and the hook is
 * inert in tests. That is a property of the test environment, not of the code, and it is why the
 * behaviour is owed a browser (spec SC-002).
 */
function scrollingAncestorOf(start: HTMLElement | null): HTMLElement | null {
  for (let el = start?.parentElement ?? null; el; el = el.parentElement) {
    const overflowY = getComputedStyle(el).overflowY;
    const scrolls = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    if (scrolls && el.scrollHeight > el.clientHeight) return el;
  }
  return null;
}
